import React, { useState, useEffect, useMemo } from 'react';
import { Calculator } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const toInches = (val, unit) => {
  const v = parseFloat(val) || 0;
  if (unit === 'cm') return v / 2.54;
  if (unit === 'ft') return v * 12;
  return v;
};

export const QuoteModal = ({ show, onClose, form, setForm, onPincodeChange, pickupValid, deliveryValid, onSubmit, loading }) => {
  const [ptlLength, setPtlLength] = useState('');
  const [ptlWidth, setPtlWidth] = useState('');
  const [ptlHeight, setPtlHeight] = useState('');
  const [ptlUnit, setPtlUnit] = useState('inch');
  const [ptlQty, setPtlQty] = useState('1');
  const [cftRate, setCftRate] = useState(0);

  useEffect(() => {
    if (!show) return;
    axios.get(`${BACKEND_URL}/api/settings/cft-rate`)
      .then(({ data }) => { setCftRate(data.cft_rate || 0); })
      .catch(() => {});
  }, [show]);

  const calc = useMemo(() => {
    if (form.transport_type !== 'PTL') return null;
    const l = toInches(ptlLength, ptlUnit);
    const w = toInches(ptlWidth, ptlUnit);
    const h = toInches(ptlHeight, ptlUnit);
    const qty = parseInt(ptlQty) || 1;
    const cft = (l * w * h / 1728) * qty;
    if (cft === 0) return null;
    const volWeight = cft * cftRate;
    return { cft, volWeight };
  }, [ptlLength, ptlWidth, ptlHeight, ptlUnit, ptlQty, form.transport_type, cftRate]);

  if (!show) return null;
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-slate-200 dark:border-white/10">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Get Instant Quote</h2>
          <p className="text-slate-600 dark:text-slate-400">Fill in your requirements</p>
        </div>
        <form onSubmit={onSubmit} className="p-6 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Name *</label>
              <input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Email *</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Phone *</label>
            <input required value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Pickup Pincode</label>
              <input value={form.pickup_pincode} onChange={(e) => onPincodeChange(e.target.value, 'pickup')} maxLength={6} className="w-full px-4 py-2 border rounded-lg" placeholder="6-digit pincode" />
              {pickupValid === true && <p className="text-xs text-green-600 mt-1 font-semibold">{form.pickup_city}, {form.pickup_state}</p>}
              {pickupValid === false && <p className="text-xs text-red-500 mt-1">Invalid pincode</p>}
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Delivery Pincode</label>
              <input value={form.delivery_pincode} onChange={(e) => onPincodeChange(e.target.value, 'delivery')} maxLength={6} className="w-full px-4 py-2 border rounded-lg" placeholder="6-digit pincode" />
              {deliveryValid === true && <p className="text-xs text-green-600 mt-1 font-semibold">{form.delivery_city}, {form.delivery_state}</p>}
              {deliveryValid === false && <p className="text-xs text-red-500 mt-1">Invalid pincode</p>}
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Transport Type</label>
              <select value={form.transport_type} onChange={(e) => setForm({...form, transport_type: e.target.value})} className="w-full px-4 py-2 border rounded-lg">
                <option value="FTL">FTL - Full Truck Load</option>
                <option value="PTL">PTL - Part Load</option>
              </select>
            </div>
            {form.transport_type === 'FTL' && (
              <div>
                <label className="block text-sm font-medium mb-2">Vehicle Type</label>
                <select value={form.vehicle_type} onChange={(e) => setForm({...form, vehicle_type: e.target.value})} className="w-full px-4 py-2 border rounded-lg">
                  <option value="Container">Container</option>
                  <option value="Open">Open</option>
                  <option value="Talpatri">Talpatri</option>
                  <option value="ODC">ODC</option>
                </select>
              </div>
            )}
          </div>

          {form.transport_type === 'PTL' && (
            <div className="p-4 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-700/30 rounded-xl space-y-3">
              <h4 className="font-bold text-amber-700 dark:text-amber-400 flex items-center gap-2"><Calculator className="w-4 h-4" />Package Dimensions</h4>
              <div className="grid grid-cols-4 gap-3">
                <div><label className="block text-xs font-medium mb-1">Length</label><input type="number" value={ptlLength} onChange={(e) => setPtlLength(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="L" /></div>
                <div><label className="block text-xs font-medium mb-1">Width</label><input type="number" value={ptlWidth} onChange={(e) => setPtlWidth(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="W" /></div>
                <div><label className="block text-xs font-medium mb-1">Height</label><input type="number" value={ptlHeight} onChange={(e) => setPtlHeight(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="H" /></div>
                <div><label className="block text-xs font-medium mb-1">Unit</label><select value={ptlUnit} onChange={(e) => setPtlUnit(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm"><option value="inch">inch</option><option value="cm">cm</option><option value="ft">ft</option></select></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-medium mb-1">No. of Packages</label><input type="number" min="1" value={ptlQty} onChange={(e) => setPtlQty(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
                <div><label className="block text-xs font-medium mb-1">Actual Weight (kg)</label><input type="number" value={form.total_weight} onChange={(e) => setForm({...form, total_weight: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
              </div>
              {calc && (
                <div className="mt-2 p-3 bg-white dark:bg-slate-800/50 rounded-lg" data-testid="quote-vol-calc">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <p className="text-xs text-slate-500 mb-1">CFT Rate</p>
                      <p className="text-lg font-bold text-slate-700 dark:text-slate-300">{cftRate}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-slate-500 mb-1">Total Volumetric Weight</p>
                      <p className="text-2xl font-black text-amber-600" data-testid="vol-weight">{calc.volWeight.toFixed(2)}</p>
                    </div>
                  </div>
                  <p className="text-xs text-slate-400 text-center mt-2">L × W × H / 1728 × CFT Rate</p>
                </div>
              )}
            </div>
          )}

          {form.transport_type !== 'PTL' && (
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Weight (kg)</label>
                <input type="number" value={form.total_weight} onChange={(e) => setForm({...form, total_weight: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Material Type</label>
                <input value={form.material_type} onChange={(e) => setForm({...form, material_type: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
              </div>
            </div>
          )}
          {form.transport_type === 'PTL' && (
            <div>
              <label className="block text-sm font-medium mb-2">Material Type</label>
              <input value={form.material_type} onChange={(e) => setForm({...form, material_type: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium mb-2">Description</label>
            <textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="w-full px-4 py-2 border rounded-lg" rows={3} />
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 px-6 py-3 border rounded-lg">Cancel</button>
            <button type="submit" disabled={loading} className="flex-1 px-6 py-3 bg-amber-500 text-white rounded-lg font-bold">{loading ? 'Submitting...' : 'Submit Request'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const SuccessModal = ({ show, onClose }) => {
  if (!show) return null;
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-md w-full p-8 text-center" onClick={(e) => e.stopPropagation()}>
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
        </div>
        <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Inquiry Received!</h3>
        <p className="text-slate-600 dark:text-slate-400 mb-6">We received your inquiry. We'll reply within 24 hours.</p>
        <div className="space-y-3 mb-6 p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
          <p className="font-bold text-slate-900 dark:text-white">For immediate assistance:</p>
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between gap-2">
              <div><p className="font-semibold">Dhaval Bhanushali</p><p className="text-slate-600">+91 87797 05742</p></div>
              <div className="flex gap-1.5">
                <a href="tel:+918779705742" className="px-3 py-2 bg-blue-500 text-white rounded-lg text-xs font-bold">Call</a>
                <a href="https://wa.me/918779705742" target="_blank" rel="noopener noreferrer" className="px-3 py-2 bg-green-500 text-white rounded-lg text-xs font-bold">WhatsApp</a>
              </div>
            </div>
            <div className="flex items-center justify-between gap-2">
              <div><p className="font-semibold">Mehul Bhanushali</p><p className="text-slate-600">+91 88507 57072</p></div>
              <div className="flex gap-1.5">
                <a href="tel:+918850757072" className="px-3 py-2 bg-blue-500 text-white rounded-lg text-xs font-bold">Call</a>
                <a href="https://wa.me/918850757072" target="_blank" rel="noopener noreferrer" className="px-3 py-2 bg-green-500 text-white rounded-lg text-xs font-bold">WhatsApp</a>
              </div>
            </div>
          </div>
        </div>
        <button onClick={onClose} className="w-full px-6 py-3 bg-amber-500 text-white rounded-lg font-bold">Close</button>
      </div>
    </div>
  );
};
